import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Thermostat, Water, Speed, Edit as EditIcon, Delete as DeleteIcon, Download as DownloadIcon, Refresh as RefreshIcon, Watch as WatchIcon, Badge as BadgeIcon, Bluetooth as BluetoothIcon, Router as RouterIcon, Nfc as NfcIcon, Sensors as SensorsIcon } from '@mui/icons-material';
import ReportsButton from '../../components/ReportsButton';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { setDevices, updateGatewayStatus, updateBeaconLocation, updateDevice } from '../../store/slices/deviceSlice';
import { useAuth } from '../../contexts/AuthContext';
import { devicesAPI, mapsAPI, hospitalsAPI, gatewayAPI } from '../../services/api';
import useWebSocket from '../../hooks/useWebSocket';
import toast from 'react-hot-toast';

function Devices() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { devices } = useSelector((state) => state.devices);
  const { tenantId, isSuperAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [locations, setLocations] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    ble_id: '',
    type: 'beacon',
    location: '',
    capabilities: [],
    hospitalId: '',
    epc: '',
    readPower: 30,
    antennaCount: 1,
  });
  const [certificateData, setCertificateData] = useState(null);
  const [loading, setLoading] = useState(false);

  const subscribedRef = useRef(false);

  // WebSocket para gateway status e localização
  const { sendMessage, readyState } = useWebSocket(null, {
    onMessage: (data) => {
      if (data.type === 'gateway_status') {
        dispatch(updateGatewayStatus({
          deviceId: data.gateway_id,
          status: data.status,
          lastSeen: data.timestamp
        }));
      } else if (data.type === 'location_update') {
        dispatch(updateBeaconLocation({
          beaconId: data.beacon_id,
          x: data.location.x,
          y: data.location.y,
          z: data.location.z || 0,
          timestamp: data.timestamp
        }));
      }
    }
  });

  // Subscribe quando conectar (apenas uma vez)
  useEffect(() => {
    if (readyState === 1 && tenantId && sendMessage && !subscribedRef.current) {
      subscribedRef.current = true;
      setTimeout(() => {
        if (readyState === 1) {
          try {
            sendMessage({
              action: 'subscribe',
              tenantId,
              subscriptions: ['gateway_status', 'location_update']
            });
          } catch (error) {
            subscribedRef.current = false;
          }
        }
      }, 1000);
    }
  }, [readyState, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHospitals = useCallback(async () => {
    try {
      const response = await hospitalsAPI.getAll();
      const hospitalsData = Array.isArray(response?.data?.data) ? response.data.data : 
                           Array.isArray(response?.data) ? response.data : [];
      setHospitals(hospitalsData);
    } catch (error) {
      console.warn('Hospitals API failed, using mock data:', error.message);
      // Fallback hospitals com tenantId atual
      const currentTenant = tenantId || 'hospital-demo-001';
      setHospitals([
        { id: currentTenant, name: 'Hospital Demo' },
        { id: 'hospital-einstein', name: 'Hospital Albert Einstein' },
        { id: 'hospital-sirio', name: 'Hospital Sírio Libanês' }
      ]);
    }
  }, [tenantId]);

  const loadLocations = useCallback(async () => {
    try {
      const response = await mapsAPI.getAll();
      const mapsData = Array.isArray(response?.data?.data) ? response.data.data : 
                      Array.isArray(response?.data) ? response.data : [];
      setLocations(mapsData.map(map => ({ id: map.id, name: map.area_name || map.name })));
    } catch (error) {
      console.warn('Maps API failed, using mock locations:', error.message);
      // Fallback locations
      setLocations([
        { id: '1', name: 'UTI - Leito 1' },
        { id: '2', name: 'Emergência - Sala 1' },
        { id: '3', name: 'Centro Cirúrgico' },
        { id: '4', name: 'Enfermaria - Ala A' },
        { id: '5', name: 'Recepção' }
      ]);
    }
  }, []);

  const loadSimulatedDevices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await devicesAPI.getAll();
      const devicesData = Array.isArray(response?.data?.data) ? response.data.data : 
                         Array.isArray(response?.data) ? response.data : [];
      dispatch(setDevices(devicesData));
    } catch (error) {
      console.error('Devices API failed:', error.message);
      toast.error('Erro ao carregar dispositivos');
      dispatch(setDevices([]));
    }
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    loadHospitals();
    loadLocations();
    loadSimulatedDevices();
  }, [loadHospitals, loadLocations, loadSimulatedDevices]);

  // Atualizar status dos beacons periodicamente
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      devices.forEach(device => {
        if (device.type === 'beacon' && device.lastSeen) {
          const lastSeenTime = new Date(device.lastSeen).getTime();
          const newStatus = (now - lastSeenTime) <= 60000 ? 'online' : 'offline';
          if (device.status !== newStatus) {
            dispatch(updateDevice({ ...device, status: newStatus }));
          }
        }
      });
    }, 10000); // Verificar a cada 10 segundos

    return () => clearInterval(interval);
  }, [devices, dispatch]);

  const handleEdit = (device) => {
    setEditingDevice(device);
    setFormData({
      name: device.name,
      ble_id: device.ble_id || '',
      type: device.type,
      location: device.area_id || device.location,
      capabilities: Array.isArray(device.capabilities) ? device.capabilities : [],
      hospitalId: device.tenantId || (isSuperAdmin ? '' : (tenantId || 'hospital-demo-001')),
      epc: device.epc || '',
      readPower: device.readPower || device.read_power || 30,
      antennaCount: device.antennaCount || device.antenna_count || 1,
    });
    setOpen(true);
  };

  const handleDelete = async (deviceId) => {
    if (window.confirm('Tem certeza que deseja excluir este dispositivo?')) {
      try {
        const device = devices.find(d => d.id === deviceId);
        if (device?.type === 'gateway') {
          await gatewayAPI.deleteGateway(deviceId);
          toast.success('Gateway e certificados excluídos com sucesso!');
        } else {
          await devicesAPI.delete(deviceId);
          toast.success('Dispositivo excluído com sucesso!');
        }
        await loadSimulatedDevices();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Erro ao excluir dispositivo');
      }
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingDevice(null);
    setFormData({
      name: '',
      ble_id: '',
      type: 'beacon',
      location: '',
      capabilities: [],
      hospitalId: isSuperAdmin ? '' : (tenantId || 'hospital-demo-001'),
      epc: '',
      readPower: 30,
      antennaCount: 1,
    });
  };

  const downloadGatewayConfig = () => {
    if (!certificateData) return;
    
    const config = `// Configuração ESP32 Gateway - ${certificateData.gateway_id}
const char* device_id = "${certificateData.gateway_id}";
const char* aws_endpoint = "${certificateData.endpoint}";

const char* certificate = R"EOF(${certificateData.certificate})EOF";

const char* private_key = R"EOF(${certificateData.private_key})EOF";

const char* aws_root_ca = R"EOF(
-----BEGIN CERTIFICATE-----
MIIDQTCCAimgAwIBAgITBmyfz5m/jAo54vB4ikPmljZbyjANBgkqhkiG9w0BAQsF
ADA5MQswCQYDVQQGEwJVUzEPMA0GA1UEChMGQW1hem9uMRkwFwYDVQQDExBBbWF6
b24gUm9vdCBDQSAxMB4XDTE1MDUyNjAwMDAwMFoXDTM4MDExNzAwMDAwMFowOTEL
MAkGA1UEBhMCVVMxDzANBgNVBAoTBkFtYXpvbjEZMBcGA1UEAxMQQW1hem9uIFJv
b3QgQ0EgMTCCASIwDQYJKoZIhvcNAQEBBQADggEPADCCAQoCggEBALJ4gHHKeNXj
ca9HgFB0fW7Y14h29Jlo91ghYPl0hAEvrAIthtOgQ3pOsqTQNroBvo3bSMgHFzZM
9O6II8c+6zf1tRn4SWiw3te5djgdYZ6k/oI2peVKVuRF4fn9tBb6dNqcmzU5L/qw
IFAGbHrQgLKm+a/sRxmPUDgH3KKHOVj4utWp+UhnMJbulHheb4mjUcAwhmahRWa6
VOujw5H5SNz/0egwLX0tdHA114gk957EWW67c4cX8jJGKLhD+rcdqsq08p8kDi1L
93FcXmn/6pUCyziKrlA4b9v7LWIbxcceVOF34GfID5yHI9Y/QCB/IIDEgEw+OyQm
jgSubJrIqg0CAwEAAaNCMEAwDwYDVR0TAQH/BAUwAwEB/zAOBgNVHQ8BAf8EBAMC
AYYwHQYDVR0OBBYEFIQYzIU07LwMlJQuCFmcx7IQTgoIMA0GCSqGSIb3DQEBCwUA
A4IBAQCY8jdaQZChGsV2USggNiMOruYou6r4lK5IpDB/G/wkjUu0yKGX9rbxenDI
U5PMCCjjmCXPI6T53iHTfIuJruydjsw2hUwsOBYy7n6Lz4J6+rpmqKyjpfxhPfzd
8aVwQ6lqez6DuEToGaPBDlhAXiHUQHpC/xxQFb0YOUIaGiQfmJbBvvyxE0PC2hg/
XvSQaqdBOceGdGFsPmXuuYU0aG3TwH+zHsh+zdOUkoG5HvmoQo9HL9hbSEUOE999
-----END CERTIFICATE-----
)EOF";
`;
    
    const blob = new Blob([config], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${certificateData.gateway_id}_config.h`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const deviceData = { 
        ...formData, 
        tenantId: isSuperAdmin ? formData.hospitalId : tenantId,
        hospitalId: isSuperAdmin ? formData.hospitalId : (tenantId || 'hospital-demo-001')
      };
      
      // For RFID tags, use EPC as both ble_id and rfid_epc
      if (formData.type === 'rfid_tag' && formData.epc) {
        deviceData.ble_id = formData.epc;
        deviceData.rfid_epc = formData.epc;
      }
      
      console.log('Device data being sent:', deviceData);
      console.log('Capabilities:', deviceData.capabilities);
      
      // Remove mac_address if it exists
      delete deviceData.mac_address;
      
      if (editingDevice) {
        await devicesAPI.update(editingDevice.id, deviceData);
        toast.success('Dispositivo atualizado com sucesso!');
      } else {
        if (formData.type === 'gateway') {
          // Create gateway with certificates
          const response = await gatewayAPI.createGateway({
            gateway_name: formData.name,
            location: formData.location,
            description: `Gateway ${formData.name}`
          });
          setCertificateData(response.data.data);
          toast.success('Gateway cadastrado com certificados!');
        } else {
          await devicesAPI.create(deviceData);
          if (formData.type === 'rfid_tag') {
            toast.success('Tag RFID cadastrada com sucesso!');
          } else {
            toast.success('Dispositivo cadastrado com sucesso!');
          }
        }
      }
      handleCloseDialog();
      await loadSimulatedDevices();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar dispositivo');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newFormData = {
      ...formData,
      [name]: name === 'capabilities' ? (Array.isArray(value) ? value : []) : value,
    };
    

    
    setFormData(newFormData);
  };

  const getSensorIcon = (capabilities, deviceType) => {
    // Show device type specific icons first
    if (deviceType === 'wristband') return <WatchIcon />;
    if (deviceType === 'badge') return <BadgeIcon />;
    if (deviceType === 'beacon') return <BluetoothIcon />;
    if (deviceType === 'gateway') return <RouterIcon />;
    if (deviceType === 'rfid_reader') return <SensorsIcon />;
    if (deviceType === 'rfid_tag') return <NfcIcon />;
    
    // Fallback to capability-based icons
    if (!capabilities || !Array.isArray(capabilities)) return null;
    if (capabilities.includes('temperature')) return <Thermostat />;
    if (capabilities.includes('humidity')) return <Water />;
    if (capabilities.includes('accelerometer')) return <Speed />;
    return null;
  };



  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'success';
      case 'offline': return 'error';
      default: return 'default';
    }
  };



  const getHospitalName = (deviceTenantId) => {
    if (!deviceTenantId) return 'Hospital não identificado';
    
    if (isSuperAdmin) {
      // Super admin pode ver nome de qualquer hospital
      const hospital = hospitals.find(h => h.id === deviceTenantId);
      return hospital ? hospital.name : deviceTenantId;
    } else {
      // Usuários normais veem apenas o nome do seu hospital
      if (deviceTenantId === tenantId) {
        const hospital = hospitals.length > 0 ? hospitals[0] : null;
        return hospital ? hospital.name : 'Meu Hospital';
      }
      return 'Hospital Externo';
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {t('devices.title')}
        </Typography>
        <Box display="flex" gap={1}>
          <ReportsButton variant="outlined" size="medium" />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSimulatedDevices}
            disabled={loading}
          >
            {t('common.update')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
          >
            {t('devices.newDevice')}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('devices.table.name')}</TableCell>
              {isSuperAdmin && <TableCell>{t('devices.table.hospital')}</TableCell>}
              <TableCell>{t('devices.table.type')}</TableCell>
              <TableCell>{t('devices.table.location')}</TableCell>
              <TableCell>{t('devices.table.capabilities')}</TableCell>
              <TableCell>{t('devices.table.status')}</TableCell>
              <TableCell>{t('devices.table.lastUpdate')}</TableCell>
              <TableCell>{t('devices.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(devices) && devices.map((device) => (
              <TableRow key={device.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    {getSensorIcon(device.capabilities, device.type)}
                    <Box ml={1}>
                      {device.equipment_name ? (
                        <Box>
                          <div style={{ fontWeight: 'bold' }}>{device.name}</div>
                          <div style={{ fontSize: '0.875rem', color: '#666' }}>Equipamento: {device.equipment_name}</div>
                        </Box>
                      ) : (
                        <Box>
                          <div style={{ fontWeight: 'bold' }}>{device.name}</div>
                          {device.type === 'rfid_tag' && device.epc && (
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>EPC: {device.epc}</div>
                          )}
                          {['beacon', 'wristband', 'badge'].includes(device.type) && device.ble_id && (
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>BLE ID: {device.ble_id}</div>
                          )}
                          {['gateway', 'rfid_reader'].includes(device.type) && device.id && (
                            <div style={{ fontSize: '0.875rem', color: '#666' }}>ID: {device.id.replace(/^gateway-/, '')}</div>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                {isSuperAdmin && (
                  <TableCell>
                    <Chip 
                      label={getHospitalName(device.tenantId)} 
                      variant="outlined" 
                      size="small"
                    />
                  </TableCell>
                )}
                <TableCell>{t(`devices.types.${device.type}`, device.type)}</TableCell>
                <TableCell>
                  {device.location_name || device.location || device.area_id || 'Não definida'}
                </TableCell>
                <TableCell>
                  {device.capabilities?.map((cap) => (
                    <Chip key={cap} label={t(`devices.capabilities.${cap}`, cap)} size="small" sx={{ mr: 0.5 }} />
                  ))}
                </TableCell>
                <TableCell>
                  <Chip
                    label={device.status === 'online' ? t('common.online') : t('common.offline')}
                    color={getStatusColor(device.status || 'offline')}
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <div>{device.last_seen || device.lastSeen ? new Date(device.last_seen || device.lastSeen).toLocaleString('pt-BR', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }) : 'Nunca'}</div>
                    {(device.battery_level || device.batteryLevel) && (
                      <Chip 
                        label={`${device.battery_level || device.batteryLevel}%`} 
                        size="small" 
                        color={(device.battery_level || device.batteryLevel) > 50 ? 'success' : (device.battery_level || device.batteryLevel) > 20 ? 'warning' : 'error'}
                        sx={{ mt: 0.5 }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEdit(device)}
                    sx={{ mr: 1 }}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(device.id)}
                  >
                    {t('common.delete')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDevice ? t('devices.editDevice') : t('devices.newDevice')}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="name"
                  label={t('devices.form.name')}
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange}
                />
              </Grid>
              {!['gateway', 'rfid_tag', 'rfid_reader'].includes(formData.type) && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="ble_id"
                    label="BLE ID"
                    fullWidth
                    required
                    value={formData.ble_id}
                    onChange={handleChange}
                    helperText="ID físico do beacon (ex: BLE-ABC123)"
                  />
                </Grid>
              )}
              {formData.type === 'rfid_tag' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="epc"
                    label="EPC"
                    fullWidth
                    required
                    value={formData.epc || ''}
                    onChange={handleChange}
                    helperText="EPC da tag RFID (ex: E2801160600002040000001F)"
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('devices.form.type')}</InputLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                  >
                    <MenuItem value="beacon">{t('devices.types.beacon')}</MenuItem>
                    <MenuItem value="wristband">Pulseira BLE</MenuItem>
                    <MenuItem value="badge">Crachá BLE</MenuItem>
                    <MenuItem value="gateway">{t('devices.types.gateway')}</MenuItem>
                    <MenuItem value="rfid_tag">Tag RFID UHF</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formData.type === 'gateway' && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>{t('devices.form.location')}</InputLabel>
                    <Select
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                    >
                      {locations.map((location) => (
                        <MenuItem key={location.id} value={location.id}>
                          {location.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12}>
                {isSuperAdmin ? (
                  <FormControl fullWidth required>
                    <InputLabel>Hospital</InputLabel>
                    <Select
                      name="hospitalId"
                      value={formData.hospitalId}
                      onChange={handleChange}
                    >
                      {hospitals.map((hospital) => (
                        <MenuItem key={hospital.id} value={hospital.id}>
                          {hospital.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    name="hospitalId"
                    label={t('devices.form.hospital')}
                    fullWidth
                    disabled
                    value={getHospitalName(tenantId || 'hospital-demo-001')}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="capabilities-label">{t('devices.form.capabilities')}</InputLabel>
                  <Select
                    labelId="capabilities-label"
                    name="capabilities"
                    multiple
                    value={formData.capabilities}
                    label={t('devices.form.capabilities')}
                    onChange={handleChange}
                  >
                    {formData.type === 'gateway' && (
                      <>
                        <MenuItem value="temperature">{t('devices.capabilities.temperature')}</MenuItem>
                        <MenuItem value="humidity">{t('devices.capabilities.humidity')}</MenuItem>
                      </>
                    )}
                    {(formData.type === 'beacon' || formData.type === 'wristband' || formData.type === 'badge') && (
                      <>
                        <MenuItem value="accelerometer">{t('devices.capabilities.accelerometer')}</MenuItem>
                        <MenuItem value="gyroscope">{t('devices.capabilities.gyroscope')}</MenuItem>
                      </>
                    )}
                    {formData.type === 'wristband' && (
                      <MenuItem value="heart_rate">Frequência Cardíaca</MenuItem>
                    )}
                    {formData.type === 'badge' && (
                      <MenuItem value="panic_button">Botão de Pânico</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained">
              {editingDevice ? t('common.update') : t('common.save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={!!certificateData} onClose={() => setCertificateData(null)} maxWidth="md" fullWidth>
        <DialogTitle>Gateway Cadastrado com Sucesso!</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Gateway ID: <strong>{certificateData?.gateway_id}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Baixe o arquivo de configuração para programar no ESP32:
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCertificateData(null)}>Fechar</Button>
          <Button 
            onClick={downloadGatewayConfig} 
            variant="contained" 
            startIcon={<DownloadIcon />}
          >
            Baixar Configuração ESP32
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Devices;